import {
  type LoadableState,
  defaultLoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type { FinancingAdapterUserCreditStatusSummary } from '~/contracts/common';
import type { DeepPartial } from '~/types';

type FinanceContextType = {
  financeInfo: LoadableState<Partial<FinancingAdapterUserCreditStatusSummary>>;
  fetchFinanceTrigger: number;
  softPullCompletedTrigger: number;
  triggerFetchFinance: () => void;
  triggerSoftPullCompleted: () => void;
};

const defaultFinanceContext: FinanceContextType = {
  financeInfo: defaultLoadableState<
    Partial<FinancingAdapterUserCreditStatusSummary>
  >({}),
  fetchFinanceTrigger: 0,
  softPullCompletedTrigger: 0,
  triggerFetchFinance() {},
  triggerSoftPullCompleted() {}
};

export const FinanceContext = createContext<FinanceContextType>(
  defaultFinanceContext
);

export function FinanceContextProvider({ children }: React.PropsWithChildren) {
  const financeInfo = useLoadableState<
    Partial<FinancingAdapterUserCreditStatusSummary>
  >({});
  const [fetchFinanceTrigger, setTrigger] = useState(
    defaultFinanceContext.fetchFinanceTrigger
  );

  const [softPullCompletedTrigger, setSoftPullCompletedTrigger] = useState(
    defaultFinanceContext.softPullCompletedTrigger
  );

  const triggerFetchFinance = useCallback(() => {
    setTrigger(Date.now());
  }, []);

  // A new soft pull changes credit status and every tile's terms, so pricing refetches too.
  const triggerSoftPullCompleted = useCallback(() => {
    const now = Date.now();

    setTrigger(now);
    setSoftPullCompletedTrigger(now);
  }, []);

  const value = useMemo<FinanceContextType>(
    () => ({
      financeInfo,
      fetchFinanceTrigger,
      softPullCompletedTrigger,
      triggerFetchFinance,
      triggerSoftPullCompleted
    }),
    [
      financeInfo,
      fetchFinanceTrigger,
      softPullCompletedTrigger,
      triggerFetchFinance,
      triggerSoftPullCompleted
    ]
  );

  return (
    <FinanceContext.Provider value={value}>{children}</FinanceContext.Provider>
  );
}

FinanceContextProvider.displayName = 'FinanceContextProvider';

export const generateFinanceContext = (
  customState: DeepPartial<FinanceContextType>
): FinanceContextType =>
  deepmerge<FinanceContextType, DeepPartial<FinanceContextType>>(
    defaultFinanceContext,
    customState
  );

export const useHasValidSoftPull = (): boolean => {
  const ctx = useContext(FinanceContext).financeInfo;

  return ctx.data.creditStatus
    ? ctx.data.creditStatus?.hasValidSoftPull
    : false;
};

export const useHasExpiredSoftpull = (): boolean => {
  const ctx = useContext(FinanceContext).financeInfo;

  return ctx.data.creditStatus
    ? ctx.data.creditStatus.hasExpiredSoftPull
    : false;
};

export const useIsFinanceLoaded = (): boolean => {
  const ctx = useContext(FinanceContext).financeInfo;

  return ctx.isLoaded;
};

export const useGetExpirationDate = (): string | null => {
  const ctx = useContext(FinanceContext).financeInfo;

  return ctx.data.creditApplicationExpirationUtc ?? null;
};

export default FinanceContext;
